<template>
  <VDropdown
    :placement="placement"
    @apply-show="handleDropdownShow"
    @apply-hide="isFocusLoopActive = false"
    :disabled="isDropdownDisabled"
  >
    <button
      class="tw-inline-flex tw-w-full tw-items-center tw-justify-center tw-space-x-2 tw-rounded-md tw-border tw-px-2 tw-py-2 tw-text-sm tw-text-neutral-700 tw-transition-colors hover:tw-border-secondary-300 hover:tw-text-neutral-900 focus-visible:tw-border-secondary-300 focus-visible:tw-text-neutral-900 focus-visible:tw-outline-none"
      :class="[
        hasSelectedValues
          ? 'tw-border-secondary-200 tw-bg-secondary-50'
          : 'tw-border-neutral-200 tw-bg-white',
      ]"
      @click="$emit('click')"
    >
      <BaseIcon
        v-if="isLocked"
        name="lock-4"
        class="tw-h-4 tw-w-4 tw-text-secondary-500"
      />
      <span><slot /></span>
      <template v-if="!isRadioSelect && hasSelectedValues">
        <span>•</span>
        <span>
          {{ filtersStateValue.length }}
        </span>
      </template>
      <span class="!tw-ml-3 tw-flex tw-grow tw-justify-end">
        <ArrowDownIcon class="tw-w-[10px] tw-text-neutral-900" />
      </span>
    </button>
    <template #popper="{ hide }">
      <div class="tw-p-4" :class="{ 'tw-pb-1': isLocked }">
        <FocusLoop :is-active="isFocusLoopActive" is-arrows-navigation-active>
          <template v-if="!activeGroup">
            <div class="tw-mb-2 tw-flex tw-items-center tw-justify-between">
              <span class="tw-text-sm tw-font-semibold tw-uppercase">
                <slot />
              </span>
              <BaseButton
                v-if="hasSelectedValues && !isRadioSelect"
                size="xs"
                color="outline-secondary"
                class="tw-inline-flex tw-items-center !tw-py-0.5"
                @click="handleClearAll"
              >
                <span>{{ filtersStateValue.length }} selected</span>
                <CloseIcon class="tw-ml-1 tw-h-3 tw-w-3" />
              </BaseButton>
            </div>
            <FormInput
              v-if="searchable"
              v-model="searchInput"
              class="tw-mb-3 tw-max-w-sm"
              placeholder="Search..."
              type="text"
            />

            <div
              v-if="hasGroups"
              class="tw-max-h-72 tw-overflow-y-auto tw-overscroll-contain tw-pr-2 xl:tw-max-h-96"
            >
              <div v-if="searchInput" class="tw-space-y-2">
                <div
                  v-for="(group, index) in resultsFromGroups"
                  :key="index"
                  class="tw-space-y-2"
                >
                  <div
                    v-for="(element, key) in group.elements"
                    :key="key"
                    class="tw-flex tw-items-center tw-justify-between tw-space-x-8 xl:tw-m-0"
                    @click="handleClick(hide)"
                  >
                    <FormCheckbox
                      v-model="filtersStateValue"
                      class="tw-my-2 tw-flex tw-items-center xl:tw-m-0"
                      :value="element.value"
                      @update:checked="sendSegmentAnalytics"
                      @input="handleChange"
                      :is-disabled="isLocked"
                      :class="{ 'tw-pointer-events-none': isLocked }"
                    >
                      <img
                        class="tw-mr-2 tw-h-8 tw-w-8 tw-rounded"
                        v-if="element.icon"
                        :src="element.icon"
                        :alt="`${element.label} logo`"
                      />
                      {{ element.label }}
                    </FormCheckbox>
                    <Tag
                      v-if="!isNaN(element.count)"
                      size="small"
                      class="!tw-py-0"
                    >
                      {{ element.count }}
                    </Tag>
                  </div>
                </div>
              </div>
              <template v-else>
                <div v-if="resultsFromGroups.length" class="tw-space-y-2">
                  <button
                    v-for="(group, index) in resultsFromGroups"
                    :key="index"
                    @click="setActiveGroup(group)"
                    class="tw-flex tw-w-full tw-items-center tw-justify-between tw-space-x-8"
                  >
                    <span
                      class="tw-inline-flex tw-items-center tw-space-x-2 tw-font-medium tw-text-neutral-600"
                    >
                      <span class="tw-text-sm">
                        {{ group.name }}
                      </span>
                      <span
                        class="tw-grid tw-h-6 tw-w-6 tw-place-items-center tw-rounded-full tw-bg-neutral-50 tw-text-xs tw-transition-colors"
                      >
                        {{ group.elements.length }}
                      </span>
                    </span>
                    <span class="tw-inline-flex tw-items-center tw-space-x-2">
                      <Tag
                        v-if="group.selectedInGroupCount > 0"
                        size="small"
                        class="!tw-py-0"
                      >
                        {{ group.selectedInGroupCount }} selected
                      </Tag>
                      <ArrowRightIcon
                        class="tw-h-3 tw-w-3 tw-text-neutral-600"
                      />
                    </span>
                  </button>
                </div>
                <div v-else class="tw-text-sm">No results found</div>
              </template>
            </div>

            <div
              v-else
              class="xl:tw-max-h-92 tw-max-h-72 tw-overflow-y-auto tw-overscroll-contain tw-pr-2"
            >
              <div v-if="visibleResults.length" class="tw-w-full tw-space-y-1">
                <div
                  v-for="(element, index) in visibleResults"
                  :key="index"
                  class="tw-my-2 tw-flex tw-items-center tw-justify-between tw-space-x-8 xl:tw-m-0"
                  @click="handleClick(hide)"
                >
                  <component
                    :is="isRadioSelect ? FormRadio : FormCheckbox"
                    v-model="filtersStateValue"
                    :value="element.value"
                    @update:checked="sendSegmentAnalytics"
                    @input="handleChange"
                    :is-disabled="isLocked"
                    class="tw-items-center"
                    :class="{ 'tw-pointer-events-none': isLocked }"
                  >
                    <img
                      v-if="element.icon"
                      :src="element.icon"
                      :alt="element.label"
                      class="tw-mr-2"
                    />
                    <span
                      v-else-if="stateKey === 'companies'"
                      class="tw-mr-2 tw-grid tw-size-8 tw-place-items-center tw-rounded-full tw-bg-secondary-300 tw-text-white"
                    >
                      {{ element.label[0] }}
                    </span>
                    <span>{{ element.label }}</span>
                  </component>
                  <Tag
                    v-if="!isNaN(element.count)"
                    size="small"
                    class="!tw-py-0"
                  >
                    {{ element.count }}
                  </Tag>
                </div>
                <InfiniteLoading
                  v-if="!isSearching"
                  @infinite="loadMore"
                  :distance="500"
                >
                  <template #spinner>
                    <div class="tw-mx-auto tw-mt-4">
                      <LoadingCircle class="tw-mx-auto" size="32" />
                    </div>
                  </template>
                  <template #no-more>
                    <span />
                  </template>
                  <template #no-results>
                    <span />
                  </template>
                  <template #error>
                    Something went wrong while loading...
                  </template>
                </InfiniteLoading>
              </div>
              <div v-else class="tw-text-sm">No results found</div>
            </div>
          </template>

          <div
            v-else
            class="tw-max-h-72 tw-overflow-y-auto tw-overscroll-contain tw-pr-2 xl:tw-max-h-96"
          >
            <BaseButton
              @click="clearActiveGroup"
              color="outline-gray"
              size="xs"
              class="tw-mb-2"
            >
              <ArrowLeftIcon class="tw-mr-1.5 tw-h-3 tw-w-3" />
              Back
            </BaseButton>
            <div class="tw-space-y-2">
              <div
                v-for="(element, key) in activeGroup.elements"
                :key="key"
                class="tw-flex tw-items-center tw-justify-between tw-space-x-8 xl:tw-m-0"
                @click="handleClick(hide)"
              >
                <FormCheckbox
                  v-model="filtersStateValue"
                  class="tw-my-2 tw-flex tw-items-center xl:tw-m-0"
                  :value="element.value"
                  @update:checked="sendSegmentAnalytics"
                  @input="handleChange"
                  :is-disabled="isLocked"
                >
                  <img
                    class="tw-mr-2 tw-h-8 tw-w-8 tw-rounded"
                    v-if="element.icon"
                    :src="element.icon"
                    :alt="`${element.label} logo`"
                  />
                  {{ element.label }}
                </FormCheckbox>
                <Tag v-if="!isNaN(element.count)" size="small" class="!tw-py-0">
                  {{ element.count }}
                </Tag>
              </div>
            </div>
          </div>
        </FocusLoop>
      </div>
      <div
        v-if="isLocked"
        class="tw-flex tw-items-center tw-px-4 tw-py-3 tw-text-xs"
        style="
          background: linear-gradient(0deg, #ecfdf3, #ecfdf3),
            linear-gradient(
              268.29deg,
              rgba(255, 199, 39, 0.1) 0%,
              rgba(36, 168, 83, 0) 100%
            );
        "
      >
        <GoldCoinIcon
          class="tw-mr-2 tw-h-5 tw-w-5 tw-rounded-full tw-border-2 tw-border-success-100 tw-text-success-900"
        />
        <span class="tw-text-success-600">
          Upgrade to
          <span class="tw-font-semibold">Pro Membership</span>
          to unlock all filters
        </span>
      </div>
    </template>
  </VDropdown>
</template>

<script setup>
import GoldCoinIcon from '@/assets/images/icons/gold-coin-2.svg'
import FormCheckbox from '../forms/FormCheckbox.vue'
import FormInput from '../forms/FormInput.vue'
import Tag from '../Tag.vue'
import BaseButton from '../BaseButton.vue'
import FormRadio from '../forms/FormRadio.vue'
import FocusLoop from '../FocusLoop.vue'
import ArrowDownIcon from '@/assets/images/icons/arrow-down.svg'
import CloseIcon from '@/assets/images/icons/close.svg'
import { ref } from 'vue'
import { useFiltersInjector } from '@/shared/composables/filters/useFiltersInjector.js'
import ArrowRightIcon from '@/assets/images/icons/arrow-right.svg'
import ArrowLeftIcon from '@/assets/images/icons/arrow-left.svg'
import BaseIcon from '@/shared/components/BaseIcon.vue'
import { UNLOCK_FULL_ACCESS_MODAL_NAME } from '@/shared/data/globalVariables'
import { useModals } from '@/shared/composables/useModals'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'

const emit = defineEmits(['change', 'click'])

const props = defineProps({
  stateKey: {
    type: String,
    required: true,
  },
  searchable: {
    type: Boolean,
    default: false,
  },
  isRadioSelect: {
    type: Boolean,
    default: false,
  },
  hasGroups: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: 'bottom-start',
  },
  isDropdownDisabled: {
    type: Boolean,
    default: false,
  },
  isLocked: {
    type: Boolean,
    default: false,
  },
})

const isFocusLoopActive = ref(false)
const { openModal } = useModals()
const {
  filtersStateValue,
  results,
  resultsFromGroups,
  clearAll,
  searchInput,
  hasSelectedValues,
  sendSegmentAnalytics,
  isSearching,
  visibleResults,
  loadMore,
  resetPageData,
  page,
} = useFiltersInjector({
  stateKey: props.stateKey,
})

const handleChange = () => {
  emit('change')
}

const handleClearAll = () => {
  clearAll()
  emit('change')
}

const handleClick = (hideMethod) => {
  if (!props.isLocked) return
  hideMethod()
  openModal(UNLOCK_FULL_ACCESS_MODAL_NAME)
}

const activeGroup = ref(null)
const setActiveGroup = (group) => {
  activeGroup.value = group
}
const clearActiveGroup = () => {
  activeGroup.value = null
}

const handleDropdownShow = () => {
  searchInput.value = ''
  resetPageData()
  isFocusLoopActive.value = true
}
</script>
